import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useCompleteTask } from '../../../../entities/task'
import { FieldWrapper, FormInput } from '../../../../shared/ui'
import { DurationSpinner } from '../../../../shared/ui/controls/DurationSpinner/DurationSpinner'
import Modal from '../../../../shared/ui/overlay/Modal/Modal'
import { round15 } from '../../../../shared/lib/round15'
import { completeTaskFormSchema, CompleteTaskFormValues } from '../model/completeTaskForm.schema'

function getNowTimeString(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

function elapsedMinutesSince(iso: string): number {
  const start = new Date(iso).getTime()
  const diff = (Date.now() - start) / 60_000
  return diff > 0 ? diff : 0
}

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  taskId: number
  plannedWorkDuration: number
  plannedDepartureDuration: number
  actualStart: string | null
  onSuccess: () => void
}

export function CompleteTaskModal({
  show,
  setShow,
  taskId,
  plannedWorkDuration,
  plannedDepartureDuration,
  actualStart,
  onSuccess,
}: Props) {
  const completeTask = useCompleteTask(taskId)

  const initialWorkDuration = actualStart
    ? round15(elapsedMinutesSince(actualStart))
    : round15(plannedWorkDuration)

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CompleteTaskFormValues>({
    resolver: zodResolver(completeTaskFormSchema),
    defaultValues: {
      actualWorkDuration: initialWorkDuration,
      endTime: getNowTimeString(),
    },
  })

  useEffect(() => {
    if (show) {
      const workDefault = actualStart
        ? round15(elapsedMinutesSince(actualStart))
        : round15(plannedWorkDuration)
      reset({
        actualWorkDuration: workDefault,
        endTime: getNowTimeString(),
      })
    }
  }, [show, plannedWorkDuration, actualStart, reset])

  const onSubmit = (values: CompleteTaskFormValues) => {
    completeTask.mutate({
      actualWorkDuration: values.actualWorkDuration,
      actualDepartureDuration: plannedDepartureDuration === 0 ? 0 : null,
      endTime: values.endTime,
    }, { onSuccess })
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Zeiterfassung"
      submitButton={{ onSubmit: handleSubmit(onSubmit), title: 'Speichern', isPending: completeTask.isPending }}
    >
      <div className="flex flex-col gap-4 w-full">
        <Controller
          name="actualWorkDuration"
          control={control}
          render={({ field }) => (
            <DurationSpinner
              label="Nettoarbeitszeit (min)"
              value={field.value}
              onChange={field.onChange}
              error={errors.actualWorkDuration?.message}
            />
          )}
        />

        <FieldWrapper label="Endzeit">
          <FormInput name="endTime" control={control} type="time" />
        </FieldWrapper>

      </div>
    </Modal>
  )
}
